import React, { useState, useEffect } from 'react';
import { Button } from './ui/Button';
import { Card } from './ui/Card';
import { Modal } from './ui/Modal';
import { twMerge } from 'tailwind-merge';

export interface UpgradePromptProps {
  promptId?: string;
  title?: string;
  message?: string;
  feature?: string;
  currentUsage?: number;
  limit?: number;
  benefits?: string[];
  show?: boolean;
  onUpgrade?: () => void;
  onDismiss?: () => void;
  onSnooze?: () => void;
  variant?: 'modal' | 'banner' | 'inline';
  urgency?: 'low' | 'medium' | 'high';
  isOpen?: boolean;
  onClose?: () => void;
  currentPlan?: string;
  current?: number;
}

const DISMISSAL_PREFIX = 'upgrade_prompt_dismissed_';
const SNOOZE_PREFIX = 'upgrade_prompt_snoozed_';
const SNOOZE_DURATION_DAYS = 7;

const generateMessage = (feature?: string, currentUsage?: number, limit?: number, _currentPlan?: string): string => {
  if (!feature) return 'Upgrade your plan to unlock more features and capabilities.';
  if (currentUsage !== undefined && limit !== undefined)
    return `You've used ${currentUsage} of your ${limit} ${feature} limit. Upgrade to get more ${feature} and unlock additional features.`;
  return `Upgrade your plan to get unlimited ${feature} and more features.`;
};

export const UpgradePrompt: React.FC<UpgradePromptProps> = ({
  promptId: providedPromptId, title: providedTitle, message: providedMessage, feature,
  currentUsage: providedCurrentUsage, limit, benefits, show: providedShow,
  onUpgrade, onDismiss: providedOnDismiss, onSnooze, variant = 'modal', urgency = 'medium',
  isOpen, onClose, currentPlan, current,
}) => {
  const promptId = providedPromptId || `upgrade_${feature}_${currentPlan || 'free'}`;
  const title = providedTitle || 'Upgrade to unlock more';
  const show = providedShow !== undefined ? providedShow : (isOpen !== undefined ? isOpen : false);
  const currentUsage = providedCurrentUsage !== undefined ? providedCurrentUsage : current;
  const onDismiss = providedOnDismiss || onClose;
  const message = providedMessage || generateMessage(feature, currentUsage, limit, currentPlan);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!show) { setIsVisible(false); return; }
    if (localStorage.getItem(`${DISMISSAL_PREFIX}${promptId}`) === 'true') { setIsVisible(false); return; }
    const snoozedUntil = localStorage.getItem(`${SNOOZE_PREFIX}${promptId}`);
    if (snoozedUntil && new Date(snoozedUntil) > new Date()) { setIsVisible(false); return; }
    setIsVisible(true);
  }, [show, promptId]);

  const handleUpgrade = () => { setIsVisible(false); onUpgrade ? onUpgrade() : (window.location.href = '/organizations/billing'); };
  const handleDismiss = () => { setIsVisible(false); localStorage.setItem(`${DISMISSAL_PREFIX}${promptId}`, 'true'); onDismiss?.(); };
  const handleSnooze = () => {
    setIsVisible(false);
    const d = new Date(); d.setDate(d.getDate() + SNOOZE_DURATION_DAYS);
    localStorage.setItem(`${SNOOZE_PREFIX}${promptId}`, d.toISOString());
    onSnooze?.();
  };

  if (!isVisible) return null;

  const usagePercentage = currentUsage !== undefined && limit !== undefined ? (currentUsage / limit) * 100 : null;
  const urgencyBorder = { low: 'border-info', medium: 'border-warning', high: 'border-danger' }[urgency];

  if (variant === 'banner') {
    return (
      <Card tone="secondary" radius="lg" padding="md" className={twMerge('border mb-4', urgencyBorder)}>
        <div className="flex items-center gap-4 flex-wrap">
          <span className="text-[32px] leading-none shrink-0">{'⭐'}</span>
          <div className="flex-1 min-w-[200px]">
            <strong className="text-text-primary block">{title}</strong>
            <p className="m-0 text-sm text-text-secondary mt-1">{message}</p>
            {feature && currentUsage !== undefined && limit !== undefined && (
              <div className="text-xs text-text-tertiary mt-1">{feature}: {currentUsage} / {limit} used ({Math.round(usagePercentage!)}%)</div>
            )}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Button variant="primary" size="sm" onClick={handleUpgrade}>Upgrade Now</Button>
            <Button variant="ghost" size="sm" onClick={handleSnooze}>Remind me later</Button>
            <Button variant="ghost" size="sm" onClick={handleDismiss} title="Dismiss" aria-label="Dismiss">{'✕'}</Button>
          </div>
        </div>
      </Card>
    );
  }

  if (variant === 'inline') {
    return (
      <Card tone="secondary" radius="lg" padding="md" className={twMerge('flex gap-3 border', urgencyBorder)}>
        <span className="text-[32px] leading-none shrink-0">{'⭐'}</span>
        <div className="flex-1">
          <strong className="text-text-primary block">{title}</strong>
          <p className="m-0 text-sm text-text-secondary mt-1">{message}</p>
          {benefits && benefits.length > 0 && (
            <ul className="m-0 mt-2 pl-0 list-none text-sm text-text-secondary">
              {benefits.slice(0, 3).map((b, i) => <li key={i} className="mb-1">{'✓'} {b}</li>)}
            </ul>
          )}
          <div className="flex gap-3 mt-3">
            <Button variant="primary" size="sm" onClick={handleUpgrade}>Upgrade</Button>
            <Button variant="ghost" size="sm" onClick={handleDismiss}>Not now</Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Modal
      isOpen={isVisible}
      onClose={handleSnooze}
      title={title}
      size="lg"
      footer={
        <>
          <Button variant="primary" size="lg" onClick={handleUpgrade}>Upgrade Now</Button>
          <Button variant="ghost" size="sm" onClick={handleDismiss}>Don&apos;t show again</Button>
        </>
      }
    >
      <div className="p-2">
        <div className="text-center mb-6">
          <div className="text-[64px] leading-none mb-4">{'⭐'}</div>
        </div>
        <div className="mb-6">
          <p className="text-sm text-text-secondary text-center leading-relaxed">{message}</p>
          {feature && currentUsage !== undefined && limit !== undefined && (
            <div className="bg-bg-tertiary rounded-lg p-4 mt-4">
              <div className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">{feature} Usage</div>
              <div className="h-2 bg-border-light rounded-full overflow-hidden">
                <div className={twMerge('h-full rounded-full transition-[width]', usagePercentage! > 90 ? 'bg-danger' : usagePercentage! > 70 ? 'bg-warning' : 'bg-accent')}
                  style={{ width: `${Math.min(usagePercentage!, 100)}%` }} />
              </div>
              <div className="text-xs text-text-secondary mt-1">{currentUsage} of {limit} used</div>
            </div>
          )}
          {benefits && benefits.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-semibold text-text-primary m-0 mb-2">With an upgrade, you&apos;ll get:</h3>
              <ul className="m-0 pl-0 list-none text-sm text-text-secondary">
                {benefits.map((b, i) => <li key={i} className="mb-1.5"><span className="text-success mr-2">{'✓'}</span>{b}</li>)}
              </ul>
            </div>
          )}
        </div>
        <div className="flex flex-col items-center gap-3">
          <Button variant="ghost" size="sm" onClick={handleSnooze}>
            Remind me in {SNOOZE_DURATION_DAYS} days
          </Button>
        </div>
      </div>
    </Modal>
  );
};

